import React, { useEffect, useState } from "react";
import axios from "../utils/api";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Typography,
  Alert,
  Button,
  IconButton,
  CircularProgress,
  Box,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { Download as DownloadIcon } from "@mui/icons-material";

// Keyframes for loader
if (typeof document !== "undefined") {
  const styleId = "sessiondetails-loader-keyframes";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes loading-bar {
        0% { transform: translateX(-60%); }
        50% { transform: translateX(10%); }
        100% { transform: translateX(120%); }
      }
    `;
    document.head.appendChild(style);
  }
}

const styles = {
  pageWrap: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: 1200,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  backButton: {
    bgcolor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      bgcolor: "#f5f5f5",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
    fontSize: { xs: "1.25rem", md: "1.5rem" },
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
    mt: 0.5,
  },
};

const SessionsTable = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await axios.get("/get-sessions");
        if (response.data.success) {
          setSessions(response.data.data);
        } else {
          setError("Failed to fetch sessions.");
        }
      } catch (err) {
        setError("An error occurred while fetching sessions.");
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, []);

  const handleConfirm = async (sessionId) => {
    setLoadingStatus((prev) => ({ ...prev, [sessionId]: "confirming" }));
    try {
      const response = await axios.patch("/sessions-status", {
        sessionId,
        status: "conformed",
      });

      if (response.data.success) {
        setSessions((prevSessions) =>
          prevSessions.map((session) =>
            session._id === sessionId ? { ...session, status: "conformed" } : session
          )
        );
      }
    } catch (error) {
      console.error("Error confirming session:", error.message);
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [sessionId]: "" }));
    }
  };

  const handleReject = async (sessionId) => {
    setLoadingStatus((prev) => ({ ...prev, [sessionId]: "rejecting" }));
    try {
      const response = await axios.patch("/sessions-status", {
        sessionId,
        status: "rejected",
      });

      if (response.data.success) {
        setSessions((prevSessions) =>
          prevSessions.map((session) =>
            session._id === sessionId ? { ...session, status: "rejected" } : session
          )
        );
      }
    } catch (error) {
      console.error("Error rejecting session:", error.message);
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [sessionId]: "" }));
    }
  };

  const handleRowClick = (sessionId) => {
    navigate(`/sessions/${sessionId}`);
  };

  const downloadCSV = () => {
    const headers = [
      "Received Date",
      "Name",
      "Company Name",
      "Email",
      "Mobile",
      "Address",
      "Booking Date",
      "Status",
    ];
    const rows = sessions.map((session) => [
      new Date(session.createdAt).toLocaleDateString("en-GB"),
      session.name,
      session.companyName,
      session.email,
      session.mobileNumber,
      session.address,
      new Date(session.bookedDate).toLocaleDateString("en-GB"),
      session.status,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "sessions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
          px: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 260,
            height: 260,
            borderRadius: "28px",
            bgcolor: "rgba(15,23,42,0.9)",
            boxShadow:
              "0 24px 60px rgba(15,23,42,0.55), 0 0 0 1px rgba(148,163,184,0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: "-40%",
              background:
                "conic-gradient(from 180deg at 50% 50%, rgba(56,189,248,0.2), rgba(129,140,248,0.35), rgba(56,189,248,0.2))",
              opacity: 0.8,
              filter: "blur(30px)",
              animation: "spin 12s linear infinite",
            }}
          />
          <Box
            sx={{
              position: "relative",
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "rgba(15,23,42,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 0 0 1px rgba(148,163,184,0.4), 0 0 0 12px rgba(15,23,42,0.9)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: -2,
                borderRadius: "50%",
                border: "3px solid transparent",
                borderTopColor: "#38bdf8",
                borderRightColor: "#6366f1",
                animation: "spin 1s linear infinite",
              }}
            />
            <Typography
              sx={{
                position: "relative",
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#e5e7eb",
              }}
            >
              S
            </Typography>
          </Box>
          <Box sx={{ position: "relative", textAlign: "center", px: 3 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: "#e5e7eb",
                mb: 0.5,
              }}
            >
              Loading sessions
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
              Fetching booking details…
            </Typography>
          </Box>
          <Box
            sx={{
              position: "relative",
              width: "68%",
              height: 4,
              borderRadius: 999,
              bgcolor: "rgba(15,23,42,1)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent, #38bdf8, #6366f1, transparent)",
                animation: "loading-bar 1.4s ease-in-out infinite",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  const cellStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "170px",
    border: "1px solid #ccc",
    padding: "8px",
    fontSize: "14px",
    textAlign: "center",
  };

  const statusCellStyle = {
    ...cellStyle,
    width: "200px",
    minHeight: "36px",
  };

  const headerStyle = {
    ...cellStyle,
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
  };

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        <Box sx={styles.header}>
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={styles.backButton}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={styles.title}>Sessions list</Typography>
            <Typography sx={styles.subtitle}>
              View and manage all booking sessions
            </Typography>
          </Box>
          <IconButton onClick={downloadCSV} color="primary" sx={styles.backButton}>
            <DownloadIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!error && (
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={headerStyle}>Received Date</TableCell>
              <TableCell style={headerStyle}>Name</TableCell>
              <TableCell style={headerStyle}>Company Name</TableCell>
              <TableCell style={headerStyle}>Email</TableCell>
              <TableCell style={headerStyle}>Mobile</TableCell>
              <TableCell style={headerStyle}>Address</TableCell>
              <TableCell style={headerStyle}>Booking Date</TableCell>
              <TableCell style={headerStyle}>Reply Mail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow
                key={session._id}
                hover
                onClick={() => handleRowClick(session._id)}
                style={{ cursor: "pointer" }}
              >
                <TableCell style={cellStyle}>
                  {new Date(session.createdAt).toLocaleDateString("en-GB")}
                </TableCell>
                <TableCell style={cellStyle}>{session.name}</TableCell>
                <TableCell style={cellStyle}>{session.companyName}</TableCell>
                <TableCell style={cellStyle}>{session.email}</TableCell>
                <TableCell style={cellStyle}>{session.mobileNumber}</TableCell>
                <TableCell style={cellStyle}>{session.address}</TableCell>
                <TableCell style={cellStyle}>
                  {new Date(session.bookedDate).toLocaleDateString("en-GB")}
                </TableCell>
                <TableCell style={statusCellStyle}>
                  {loadingStatus[session._id] === "confirming" ||
                  loadingStatus[session._id] === "rejecting" ? (
                    <CircularProgress size={24} />
                  ) : session.status === "pending" ? (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        style={{ marginRight: "8px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirm(session._id);
                        }}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(session._id);
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  ) : session.status === "conformed" ? (
                    <Typography variant="body2" color="success.main">
                      Confirmed
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="error.main">
                      Rejected
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default SessionsTable;
